import { useTranslation } from "react-i18next";
import cn from "classnames";
import type { RangeValue } from "@/shared/ui";
import type { BudgetSummary, BudgetBalance } from "../../../types";
import { formatCurrencyNoDecimals, clampProgressWidth, parseMoney, getSectionColorClasses } from "../../../utils";
import PeriodPicker from "./PeriodPicker";

interface Props {
  data: BudgetSummary;
  balance: BudgetBalance | null;
  dateRange: RangeValue;
  onDateRangeChange: (range: RangeValue) => void;
}

export default function BalanceOverview({ data, balance, dateRange, onDateRangeChange }: Props) {
  const { t } = useTranslation("FinancePage");

  const usedRaw = balance?.used ?? data.grand_total;
  const grandTotal = parseMoney(usedRaw);

  const budgetStr = balance != null && balance.mode !== "used_only" ? balance.budget : null;

  if (budgetStr == null) {
    return (
      <div className="radius-3xl surface-base-fill py-5 px-6 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <span className="text-label-sm content-action-neutral">{t("balance.title")}</span>
          <PeriodPicker value={dateRange} onChange={onDateRangeChange} />
        </div>
        <div className="w-full flex flex-col gap-1">
          <span className="text-label-xs content-base-secondary">{t("balance.used")}</span>
          <div className="flex justify-start items-end gap-1">
            <span className="text-display-sm content-base-primary">
              {formatCurrencyNoDecimals(grandTotal, false)}
            </span>
            <span className="text-display-xs content-base-secondary">₸</span>
          </div>
        </div>
      </div>
    );
  }

  const totalBudget = parseMoney(budgetStr);
  const remainingValue = balance?.remaining != null ? parseMoney(balance.remaining) : totalBudget - grandTotal;
  const executionPct = totalBudget > 0 ? (grandTotal / totalBudget) * 100 : 0;

  const sectionSegments = data.sections.map(function buildSegment(section) {
    const sectionTotal = parseMoney(section.total);
    const share = totalBudget > 0 ? (sectionTotal / totalBudget) * 100 : 0;
    return {
      id: section.id,
      share: clampProgressWidth(share),
      fillClass: getSectionColorClasses(section.id).fill,
    };
  });

  return (
    <div className="radius-3xl surface-base-fill py-5 px-6 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <span className="text-label-sm content-action-neutral">{t("balance.title")}</span>
        <PeriodPicker value={dateRange} onChange={onDateRangeChange} />
      </div>

      <div className="w-full flex justify-between items-end gap-4 mb-5">
        <div className="flex justify-start items-end gap-1">
          <span className="text-display-sm content-base-primary">
            {formatCurrencyNoDecimals(remainingValue, false)}
          </span>
          <span className="text-display-xs content-base-secondary">₸</span>
        </div>
        <span className="text-heading-lg content-base-low">{executionPct.toFixed(0)}%</span>
      </div>

      <div className="flex gap-0.5 mb-2">
        {sectionSegments.map(function renderSegment(seg) {
          return (
            <div
              key={seg.id}
              className={cn("h-1.5 radius-2xs", seg.fillClass)}
              style={{ width: `${seg.share}%` }}
            />
          );
        })}
        <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
      </div>

      <div className="flex justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-label-xs content-base-secondary">{t("balance.budget")}</span>
          <p className="text-body-bold-sm content-base-primary">{formatCurrencyNoDecimals(totalBudget)}</p>
        </div>
        <div className="flex flex-col gap-1 text-right">
          <span className="text-label-xs content-base-secondary">{t("balance.used")}</span>
          <p className="text-body-bold-sm content-base-primary">{formatCurrencyNoDecimals(grandTotal)}</p>
        </div>
      </div>
    </div>
  );
}
